// src/components/common/Sidebar.tsx
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  SparklesIcon,
  ChartBarIcon,
  UserIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  BookOpenIcon,
  UsersIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
  const location = useLocation();

  // FLAT navigation - no complex nesting
  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      description: 'Your career overview'
    },
    {
      name: 'Interview Practice',
      href: '/interview',
      icon: SparklesIcon,
      description: 'AI-powered coaching',
      badge: 'POPULAR',
      badgeColor: 'bg-purple-100 text-purple-700'
    },
    {
      name: 'Resume Builder',
      href: '/resume',
      icon: DocumentTextIcon,
      description: 'Create AI resumes',
      badge: 'HOT',
      badgeColor: 'bg-red-100 text-red-700'
    },
    {
      name: 'Job Applications',
      href: '/career/applications',
      icon: BriefcaseIcon,
      description: 'Track your applications',
      badge: 'BETA',
      badgeColor: 'bg-blue-100 text-blue-700'
    },
    {
      name: 'Skill Assessment',
      href: '/career/assessment',
      icon: AcademicCapIcon,
      description: 'Discover your strengths',
      badge: 'BETA',
      badgeColor: 'bg-blue-100 text-blue-700'
    },
    {
      name: 'Interview Resources',
      href: '/resources/interview',
      icon: LightBulbIcon,
      description: 'Interview knowledge base',
      badge: 'NEW',
      badgeColor: 'bg-green-100 text-green-700'
    },
    {
      name: 'Learning',
      href: '/learning',
      icon: BookOpenIcon,
      description: 'Skill development',
      badge: 'SOON',
      badgeColor: 'bg-gray-100 text-gray-600',
      comingSoon: true
    },
    {
      name: 'Network',
      href: '/network',
      icon: UsersIcon,
      description: 'Professional connections',
      badge: 'SOON', 
      badgeColor: 'bg-gray-100 text-gray-600',
      comingSoon: true
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: ChartBarIcon,
      description: 'Progress tracking'
    },
    {
      name: 'Profile',
      href: '/profile',
      icon: UserIcon,
      description: 'Account settings'
    },
  ];

  const isActive = (href: string) => {
    if (href === '/interview') {
      return location.pathname.includes('/interview');
    }
    if (href === '/resume') {
      return location.pathname.includes('/resume');
    }
    if (href === '/career/applications') {
      return location.pathname.includes('/career/applications');
    }
    if (href === '/career/assessment') {
      return location.pathname.includes('/career/assessment') || location.pathname.includes('/career/skills');
    }
    if (href === '/resources/interview') {
      return location.pathname.includes('/resources/interview');
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleClick = (item: any, e: React.MouseEvent) => {
    if (item.comingSoon) {
      e.preventDefault();
      // Simple coming soon notification
      alert(`${item.name} launches Q3 2025! We'll notify you when it's ready.`);
    }
  };

  return (
    <div className={`bg-white shadow-lg min-h-screen border-r border-gray-100 transition-all duration-300 ${
      isOpen ? 'w-64' : 'w-16'
    }`}>
      {/* Collapse Toggle */}
      <div className="p-3 border-b border-gray-100 flex justify-end">
        <button
          onClick={onToggle}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          {isOpen ? (
            <ChevronLeftIcon className="w-4 h-4" />
          ) : (
            <ChevronRightIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Modern Navigation */}
      <nav className="px-2 py-4">
        <ul className="space-y-1">
          {navigationItems.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 relative ${
                  isActive(item.href)
                    ? 'bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 shadow-sm border border-purple-100'
                    : item.comingSoon
                    ? 'text-gray-400 hover:text-gray-500 hover:bg-gray-50'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
                onClick={(e) => handleClick(item, e)}
                title={!isOpen ? item.name : ''}
              >
                <item.icon className={`${isOpen ? 'mr-3' : 'mx-auto'} h-5 w-5 flex-shrink-0 ${
                  isActive(item.href) ? 'text-purple-600' : 'text-gray-500'
                }`} />
                
                {/* Active indicator dot for collapsed state */}
                {!isOpen && isActive(item.href) && (
                  <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-purple-600 rounded-full"></div>
                )}
                {isOpen && (
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="truncate font-medium">{item.name}</span>
                      {item.badge && (
                        <span className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${item.badgeColor} shadow-sm`}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {item.description}
                    </div>
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Modern CTA - Only when sidebar is open */}
      {isOpen && location.pathname === '/dashboard' && (
        <div className="mx-2 mb-4 p-3 bg-gradient-to-br from-purple-50 via-blue-50 to-teal-50 border border-purple-200 rounded-xl shadow-sm">
          <div className="flex items-center mb-2">
            <SparklesIcon className="w-4 h-4 text-purple-600 mr-2" />
            <h3 className="text-sm font-semibold text-purple-900">
              Start Your Journey
            </h3>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            AI-powered interview coaching with real-time feedback
          </p>
          <Link
            to="/interview"
            className="block w-full text-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-sm"
          >
            Start Interview Practice
          </Link>
        </div>
      )}

      {/* Compact Status - Only when sidebar is open */}
      {isOpen && (
        <div className="mx-2 mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-xs font-medium text-gray-900 mb-2">Platform Status</div>
          <div className="text-xs text-gray-600 space-y-1">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
              Interview & Resume ready
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
              Job tracking in beta
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
              Learning & Network Q3
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;